/**
 * Tenant URL utilities for the {slug}.step.uz subdomain architecture.
 * Phase 1 of ddcsbdmn.md — tenant detection from URL hostname.
 */

const STEP_DOMAIN = "step.uz";

// Subdomains that are NOT tenant subdomains — reserved for platform infra
const SYSTEM_SUBDOMAINS = new Set(["app", "admin", "api", "support", "www", "mail", "smtp"]);

/**
 * Extracts the tenant slug from the current URL hostname.
 * Returns null when running on localhost, a system subdomain, or non-step.uz domain.
 *
 * Examples:
 *   aziz-tools.step.uz  → "aziz-tools"
 *   app.step.uz         → null  (system subdomain)
 *   localhost           → null  (dev)
 *   step.uz             → null  (root domain — marketing site)
 */
export function getTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname;

  // Dev / direct IP
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return null;
  }

  if (!hostname.endsWith(`.${STEP_DOMAIN}`)) return null;

  const subdomain = hostname.slice(0, -(`.${STEP_DOMAIN}`.length));
  if (!subdomain || SYSTEM_SUBDOMAINS.has(subdomain)) return null;

  return subdomain;
}

/**
 * True when running on a real tenant subdomain (e.g. aziz-tools.step.uz).
 */
export function isTenantSubdomain(): boolean {
  return getTenantSlug() !== null;
}

/**
 * True when running on app.step.uz (the legacy / migration entrypoint).
 */
export function isAppSubdomain(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === `app.${STEP_DOMAIN}`;
}

/**
 * Builds the full HTTPS URL for a tenant slug.
 * getFullTenantUrl("aziz-tools") → "https://aziz-tools.step.uz"
 */
export function getFullTenantUrl(slug: string): string {
  return `https://${slug}.${STEP_DOMAIN}`;
}

/**
 * True when the user is already on the correct tenant subdomain
 * for the given slug (no redirect needed).
 */
export function isOnCorrectTenantUrl(slug: string): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === `${slug}.${STEP_DOMAIN}`;
}
